import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  IMediaItem,
  MediaItemSaveRequest,
  MediaItemSource,
  SaveResponse,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import MediaLibraryItemForm, {
  MediaLibraryItemFormValues,
} from "./MediaLibraryItemForm";
import { ContextOrganizationFieldMode } from "components/ContextOrganizationField";
import { MediaFilterType } from "utils/mediaUtils";

type MediaLibraryItemUpdateFormProps = {
  source?: MediaItemSource;
  options?: MediaFilterType[];
  mediaItem: IMediaItem;
  alternativeTextRequired?: boolean;
  customAlternativeTextLabel?: string;
  onMediaLibraryItemUpdated: () => void;
  onCancel: () => void;
};

const MediaLibraryItemUpdateForm = ({
  source = MediaItemSource.MediaLibrary,
  options,
  mediaItem,
  alternativeTextRequired,
  customAlternativeTextLabel,
  onMediaLibraryItemUpdated,
  onCancel,
}: MediaLibraryItemUpdateFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { mediaService } = useGlobalContext();
  const queryClient = useQueryClient();

  const onSubmit = async (values: MediaLibraryItemFormValues) => {
    try {
      const updatedMedia: SaveResponse = await registerPromise(
        mediaService.saveItem(
          source,
          MediaItemSaveRequest.fromJS({
            ...values,
          })
        )
      );

      queryClient.invalidateQueries("media-items");
      queryClient.invalidateQueries(["media-item", values.id]);
      onMediaLibraryItemUpdated();
      return updatedMedia;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <MediaLibraryItemForm
      source={source}
      options={options}
      mediaLibraryItem={mediaItem}
      alternativeTextRequired={alternativeTextRequired}
      customAlternativeTextLabel={customAlternativeTextLabel}
      organizationPickerMode={ContextOrganizationFieldMode.EditDisabled}
      onSubmit={onSubmit}
      submitLabel={t({ id: "mediaLibraryItemFormUpdateTitle" })}
      onCancel={onCancel}
    />
  );
};

export default MediaLibraryItemUpdateForm;
